import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Menu, X } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useState, useMemo, useEffect, useContext, useCallback } from 'react'
import { useNavigate } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import FeedbackButton from '@/components/FeedbackButton/FeedbackButton'
import StatusIcon, { STATUS as ICON_STATUS } from '@/components/StatusIcon/StatusIcon'
import { useInstructorView } from '@/pages/Accounts/Instructors/Students/InstructorViewContext'
import {
  completeCourseContent,
  getStudentCourseContentRecords,
  startCourseContent,
} from '@/services/student_courses.service'
import { RootStoreContext } from '@/stores/RootStore'
import { getNextIncompleteCourseRecord } from '@/utils/certificates'
import { cn, toast } from '@/utils/helpers'

import BlockArticulate from './BlockArticulate'
import BlockPDF from './BlockPDF'
import BlockQuiz from './BlockQuiz'
import BlockVideo from './BlockVideo'
import CourseProgressBar from './CourseProgressBar'
import { encouragementForAdvance } from './encouragement'

const STATUS = {
  ...ICON_STATUS,
  QUIZ_PENDING: 'Quiz Pending',
}
const QUIZ_BLOCK_ID = 'QUIZ'

const CourseModal = observer(
  ({ closeModal, courseRecord, entityId, initialCourseContent, banner }) => {
    // Context
    const { course: courseContext } = useContext(RootStoreContext)
    const { canExit, setCourse, setCourseContent } = courseContext
    const navigate = useNavigate()
    const instructorView = useInstructorView()
    const isInstructorView = !!instructorView

    useEffect(() => {
      setCourse(courseRecord?.course?.id)
      return () => setCourse(null)
    }, [courseRecord, setCourse])

    const queryKey = ['accounts', 'students', 'courses', courseRecord.id]

    const {
      data: courseContentRecords,
      isPending,
      refetch,
    } = useQuery({
      queryKey,
      queryFn: async () => {
        const response = await getStudentCourseContentRecords(entityId, courseRecord.id, {
          // Students get a bare prior-attempt count back — BlockQuiz uses it
          // to number the current attempt in the budget banner.
          includeQuizAttempts: true,
        })
        return response.results
      },
      // Links expire after 1 hour, so if the user focuses on the tab after that, we want to refetch
      refetchOnWindowFocus: true,
      staleTime: 60 * 60 * 1000, // 60 minutes
      // TODO: Do we need refetch course data when switching between blocks?
    })

    // State
    const [block, setBlock] = useState(0)
    const [hasResumed, setHasResumed] = useState(false)
    const [initialStatus] = useState(courseRecord.status)
    const [sidebarOpen, setSidebarOpen] = useState(false)

    const courseIsCompleted = courseRecord.status === 'Completed'

    // Data
    const handleError = (m) => toast(m, 'error')
    const queryClient = useQueryClient()

    const { mutate: _startCourseContentMutation } = useMutation({
      mutationFn: (/** @type {string} */ blockId) => startCourseContent(entityId, blockId),
      onError: (err) => handleError(err.message),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey })
      },
    })
    const { mutate: _completeCourseContentMutation } = useMutation({
      mutationFn: (/** @type {string} */ blockId) => completeCourseContent(entityId, blockId),
      onError: (err) => handleError(err.message),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey })
      },
    })

    // Instructors viewing a student's record never write — gate at the boundary
    // here (rather than at each call site) so a future caller can't accidentally
    // reintroduce a write path. quiz submission is similarly neutered in BlockQuiz.
    const startCourseContentMutation = useMemo(
      () => (isInstructorView ? () => {} : _startCourseContentMutation),
      [isInstructorView, _startCourseContentMutation],
    )
    const completeCourseContentMutation = useMemo(
      () => (isInstructorView ? () => {} : _completeCourseContentMutation),
      [isInstructorView, _completeCourseContentMutation],
    )

    const blocks = useMemo(() => {
      if (!courseContentRecords) return new Map()
      const _blocks = new Map()

      courseContentRecords.forEach((blockObj) => {
        // Don't include quiz sections if the course is completed
        if (blockObj.content.type === 'Quiz Only' && courseIsCompleted) return
        _blocks.set(`content-${blockObj.id}`, { ...blockObj, contentId: `content-${blockObj.id}` })
        // Don't include quiz sections if the course is completed
        if (courseIsCompleted) {
          return
        }
        if (blockObj.content.quiz && blockObj.content.type !== 'Quiz Only')
          _blocks.set(`quiz-${blockObj.id}`, {
            ...blockObj,
            content: { ...blockObj.content, type: QUIZ_BLOCK_ID },
            contentId: `quiz-${blockObj.id}`,
          })
      })
      return _blocks
    }, [courseContentRecords, courseIsCompleted])

    const checkCompletion = () => {
      if (activeBlock.completedAt) return // Already completed
      if (activeBlock.content.type === QUIZ_BLOCK_ID) return // Don't complete quiz blocks
      completeCourseContentMutation(activeBlock.id)
    }

    // Track furthest block reached so revisits and TOC backward-nav don't
    // re-fire celebrations. Seeded from the active block index by the effect
    // below so resuming mid-course doesn't treat the resume target as new.
    const [maxBlockReached, setMaxBlockReached] = useState(0)

    // Articulate courses report completion via the xAPI endpoint, and update the content record.
    // For those cases, we need to skip the completion check here, and refetch instead to get the updated status.
    const nextBlock = (
      { completionTriggeredOnServer } = { completionTriggeredOnServer: false },
    ) => {
      if (!completionTriggeredOnServer) checkCompletion()
      else refetch()

      // Finishing the last block completes the course. Keep momentum by sending
      // the student to the next incomplete module if there is one; otherwise
      // close the viewer to land on this course's detail page (its results)
      // rather than bouncing to the course list with a redundant "see your
      // results" toast. Instructors viewing a student's progress stay put — the
      // parent controls navigation (and there's nothing past the last block).
      if (block >= blocks.size - 1) {
        if (isInstructorView) return
        const nextCourse = getNextIncompleteCourseRecord(courseRecord)
        // Always close before navigating. The course page stays mounted across
        // the courseId param change, so a still-open player would remount
        // against the next module before its content records exist (they're
        // only created by the course record's `start` endpoint) and render an
        // empty white player. Closing lands the student on the next module's
        // overview — the same place the completion page's "Start Next Module"
        // CTA goes — where Start creates the records (and the per-module
        // waiver acknowledgement still happens).
        closeModal()
        if (nextCourse) {
          toast(
            `Module complete — up next: ${nextCourse.course?.name ?? 'the next module'}`,
            'success',
          )
          navigate(`/accounts/${entityId}/students/courses/${nextCourse.id}`)
        }
        return
      }

      // Celebrate genuine forward progress for the student on non-final advances.
      // Skip for instructors (no progress), revisits (already past this block),
      // and quiz blocks (BlockQuiz already gets its own acknowledgement).
      const isQuizBlock = activeBlock?.content?.type === QUIZ_BLOCK_ID
      const isNewAdvance = block + 1 > maxBlockReached
      if (!isInstructorView && isNewAdvance && !isQuizBlock) {
        const completedAfter = Math.min(completedSteps + 1, totalSteps)
        toast(encouragementForAdvance(completedAfter, totalSteps), 'success')
      }
      // maxBlockReached is bumped by the effect below in response to setBlock.
      setBlock((prev) => prev + 1)
    }
    const isLastBlock = block === blocks.size - 1

    const goToBlockWithId = useCallback(
      (id) => {
        const index = [...blocks.keys()].findIndex((blockId) => blockId === id)
        if (index !== -1) setBlock(index)
      },
      [blocks],
    )

    const activeBlock = useMemo(() => {
      const blockId = [...blocks.keys()][block]
      return blocks.get(blockId) || null
    }, [block, blocks])

    useEffect(() => {
      setCourseContent(activeBlock?.content?.id)
      return () => setCourseContent(null)
    }, [activeBlock, setCourseContent])

    // Anywhere setBlock fires — resume, TOC click, nextBlock — bump the
    // high-water mark so backward navigation and resumes don't celebrate
    // previously-completed steps.
    useEffect(() => {
      setMaxBlockReached((prev) => Math.max(prev, block))
    }, [block])

    // Mark content as started when user views it
    useEffect(() => {
      if (!courseContentRecords || !activeBlock) return
      // Don't track quiz blocks
      if (activeBlock.content.type === QUIZ_BLOCK_ID) return
      const record = courseContentRecords.find((r) => r.id === activeBlock.id)
      // Should this be based on status instead of startedAt?
      if (record && !record.startedAt) {
        startCourseContentMutation(activeBlock.id)
      }
    }, [courseContentRecords, activeBlock, startCourseContentMutation])

    // Go to first incomplete block when modal opens
    useEffect(() => {
      if (!courseContentRecords || hasResumed) return
      if (initialCourseContent) {
        goToBlockWithId(initialCourseContent)
        setHasResumed(true)
        return
      }
      const firstIncomplete = [...blocks.entries()].find(([, blockObj]) => {
        if (blockObj.status === null) return true
        if (blockObj.content.type === QUIZ_BLOCK_ID) return blockObj.status === STATUS.QUIZ_PENDING
        if (blockObj.content.type === 'Quiz Only' && blockObj.status === STATUS.QUIZ_PENDING)
          return true
        return [STATUS.NOT_STARTED, STATUS.IN_PROGRESS].includes(blockObj.status)
      })

      if (firstIncomplete) {
        goToBlockWithId(firstIncomplete[0])
      }
      setHasResumed(true)
      // TODO: Do we need to handle all completed?
    }, [blocks, courseContentRecords, goToBlockWithId, hasResumed, initialCourseContent])

    // Close modal when the course is newly completed
    useEffect(() => {
      const closeModalAndReset = () => {
        setHasResumed(false)
        closeModal()
      }
      if (initialStatus === 'Completed') return
      if (courseRecord.status === 'Completed') {
        closeModalAndReset()
      }
    }, [closeModal, courseRecord.status, initialStatus])

    const contentStatus = (blockObj) => {
      if (!blockObj.status || blockObj.status === STATUS.NOT_STARTED) return STATUS.NOT_STARTED
      if (blockObj.status === STATUS.IN_PROGRESS) return STATUS.IN_PROGRESS
      if (blockObj.content.type === 'Quiz Only' && blockObj.status === STATUS.QUIZ_PENDING)
        return STATUS.IN_PROGRESS
      return STATUS.COMPLETED
    }
    const quizStatus = (blockObj) => {
      if (blockObj.status === STATUS.COMPLETED) return STATUS.COMPLETED
      if (blockObj.status === STATUS.QUIZ_PENDING) return STATUS.IN_PROGRESS
      return STATUS.NOT_STARTED
    }

    // Assumes that the review material button just goes back to the content
    // block, and Quiz Only blocks go to the first block
    const reviewMaterial = () => {
      if (activeBlock.content.type === QUIZ_BLOCK_ID) {
        setBlock((prev) => prev - 1)
      } else {
        setBlock(0)
      }
    }

    const tocData = useMemo(() => {
      if (!courseContentRecords || !activeBlock) return []
      return courseContentRecords
        .map((blockObj) => {
          if (courseIsCompleted && blockObj.content.type === 'Quiz Only') {
            return null
          }
          const cStatus = contentStatus(blockObj)
          // The block's own name is the "view the material" step — there's no
          // separate generic "Content" row. A Quiz hangs off it as a sub-step.
          const content = {
            id: `content-${blockObj.id}`,
            title: blockObj.content.name ?? blockObj.content.type,
            active: `content-${blockObj.id}` === activeBlock.contentId,
            status: cStatus,
            subtitle: (() => {
              if (cStatus === STATUS.COMPLETED) return 'Done'
              if (cStatus === STATUS.IN_PROGRESS) return 'Resume'
              return null
            })(),
          }
          const hasQuiz =
            !courseIsCompleted && blockObj.content.quiz && blockObj.content.type !== 'Quiz Only'
          let quiz = null
          if (hasQuiz) {
            const qStatus = quizStatus(blockObj)
            const { quiz: quizMeta } = blockObj.content
            quiz = {
              id: `quiz-${blockObj.id}`,
              title: 'Quiz',
              active: `quiz-${blockObj.id}` === activeBlock.contentId,
              status: qStatus,
              subtitle: (() => {
                if (qStatus === STATUS.COMPLETED) return 'Passed'
                if (quizMeta?.numQuestionsToPass && quizMeta?.numQuestionsToDisplay) {
                  return `Pass ${quizMeta.numQuestionsToPass}/${quizMeta.numQuestionsToDisplay}`
                }
                return null
              })(),
            }
          }
          return { id: blockObj.id, content, quiz }
        })
        .filter(Boolean)
    }, [courseContentRecords, activeBlock, courseIsCompleted])

    // Progress + up-next derivations for the header bar and Block footer hint.
    // "Step" = the block's content step plus its Quiz (when present); a single
    // content record can contribute two. The content step's label is the block
    // name; the quiz reads as "<block> · Quiz".
    const flatTocItems = useMemo(
      () =>
        tocData.flatMap((s) => {
          const steps = [{ ...s.content, label: s.content.title }]
          if (s.quiz) steps.push({ ...s.quiz, label: `${s.content.title} · Quiz` })
          return steps
        }),
      [tocData],
    )
    const totalSteps = flatTocItems.length
    const completedSteps = flatTocItems.filter((i) => i.status === STATUS.COMPLETED).length
    const activeIndex = flatTocItems.findIndex((i) => i.active)
    const currentStep =
      activeIndex >= 0 ? activeIndex + 1 : Math.min(completedSteps + 1, totalSteps)
    const upNextItem = activeIndex >= 0 ? flatTocItems[activeIndex + 1] : null
    const upNextTitle = upNextItem ? upNextItem.label : null

    // Header names: the user-facing Course/Certificate is the headline; the
    // module being taken is the subtitle. courseRecord has no `name` of its own —
    // both come from already-expanded relations.
    const certName = courseRecord.studentCertificate?.certificate?.name
    const moduleName = courseRecord.course?.name
    const headerTitle = certName ?? moduleName ?? 'Course'
    const moduleSubtitle = certName && moduleName && certName !== moduleName ? moduleName : null

    // One TOC step (the block's content step or its Quiz): status icon, a
    // clickable label, and an optional subtitle. Not-started steps are locked
    // for students so they can't jump ahead.
    const renderTocStep = (item) => (
      <div className="flex items-start gap-2 py-0.5">
        <StatusIcon status={item.status} size="small" />
        <div className="flex flex-1 flex-col leading-tight">
          <button
            type="button"
            className={cn(
              'cursor-pointer text-left text-sm disabled:cursor-not-allowed',
              item.active && 'font-bold',
              item.status === STATUS.COMPLETED && 'text-success',
              item.status === STATUS.NOT_STARTED && 'text-gray-500',
            )}
            onClick={() => {
              goToBlockWithId(item.id)
              setSidebarOpen(false)
            }}
            disabled={!isInstructorView && item.status === STATUS.NOT_STARTED}
          >
            {item.title}
          </button>
          {item.subtitle && <span className="text-muted-foreground text-xs">{item.subtitle}</span>}
        </div>
      </div>
    )

    if (isPending || !courseContentRecords) {
      return null
    }

    return (
      <Dialog open onClose={() => closeModal()}>
        <DialogContent className="border-primary flex h-[100dvh] w-full !max-w-full flex-col gap-0 overflow-hidden rounded-none p-0 lg:h-[95vh] lg:!max-w-[95vw] lg:rounded-lg">
          <DialogHeader className="bg-primary flex flex-col gap-2 p-1 text-white lg:p-4">
            <div className="flex flex-row items-start justify-between gap-2">
              {/* Mobile sidebar toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 text-white hover:bg-white/20 lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-base leading-tight text-white lg:text-lg">
                  {headerTitle}
                </DialogTitle>
                {moduleSubtitle ? (
                  <DialogDescription className="text-sm text-white/80">
                    {moduleSubtitle}
                  </DialogDescription>
                ) : (
                  <DialogDescription className="sr-only">Course content</DialogDescription>
                )}
              </div>
              <div className="shrink-0">
                {canExit && (
                  <Button
                    disabled={!courseIsCompleted && !canExit}
                    variant="secondary"
                    onClick={() => closeModal()}
                  >
                    {courseIsCompleted ? 'Exit' : 'Save & Exit'}
                  </Button>
                )}
                {!canExit && (
                  <ConfirmButton
                    variant="secondary"
                    onClick={() => closeModal()}
                    confirmTitle="Exit Course"
                    confirmContent="Are you sure you want to exit the course? Your quiz progress will not be saved."
                  >
                    Exit
                  </ConfirmButton>
                )}
              </div>
            </div>
            {totalSteps > 0 && (
              <CourseProgressBar
                current={completedSteps}
                total={totalSteps}
                label={`Step ${currentStep} of ${totalSteps} · ${completedSteps} complete`}
              />
            )}
          </DialogHeader>
          {banner}
          <div className="relative flex flex-1 overflow-hidden">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
              />
            )}
            {/* Sidebar - slide-in on mobile, always visible on desktop */}
            <nav
              aria-label="Course content"
              className={cn(
                'bg-secondary absolute inset-y-0 left-0 z-50 flex w-72 flex-col justify-between gap-4 border-r p-4 transition-transform duration-300 ease-in-out lg:relative lg:z-auto lg:w-2xs lg:translate-x-0 lg:rounded lg:border lg:border-r-0',
                sidebarOpen ? 'translate-x-0' : '-translate-x-full',
              )}
            >
              <div className="overflow-y-auto">
                <ol className="list-decimal ps-6">
                  {tocData.map((section) => (
                    <li key={section.id} className="mb-2">
                      {renderTocStep(section.content)}
                      {section.quiz && <div className="ps-6">{renderTocStep(section.quiz)}</div>}
                    </li>
                  ))}
                </ol>
              </div>

              <FeedbackButton />
            </nav>
            {/* Main content area - full width on mobile */}
            <div className="flex-1 overflow-y-auto p-2 lg:p-4" data-testid="active-block">
              {activeBlock?.content && activeBlock.content.type === 'PDF' && (
                <BlockPDF
                  key={activeBlock.contentId}
                  block={activeBlock}
                  nextBlock={nextBlock}
                  isLastBlock={isLastBlock}
                  upNextTitle={upNextTitle}
                />
              )}
              {activeBlock?.content &&
                [QUIZ_BLOCK_ID, 'Quiz Only'].includes(activeBlock.content.type) && (
                  <BlockQuiz
                    key={activeBlock.contentId}
                    block={activeBlock}
                    nextBlock={nextBlock}
                    reviewMaterial={reviewMaterial}
                    entityId={entityId}
                    isLastBlock={isLastBlock}
                    upNextTitle={upNextTitle}
                  />
                )}
              {activeBlock?.content && activeBlock.content.type === 'Articulate' && (
                <BlockArticulate
                  key={activeBlock.contentId}
                  block={activeBlock}
                  nextBlock={nextBlock}
                  isLastBlock={isLastBlock}
                  upNextTitle={upNextTitle}
                />
              )}
              {activeBlock?.content && activeBlock.content.type === 'Video' && (
                <BlockVideo
                  key={activeBlock.contentId}
                  block={activeBlock}
                  nextBlock={nextBlock}
                  isLastBlock={isLastBlock}
                  upNextTitle={upNextTitle}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  },
)

export default CourseModal

CourseModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  courseRecord: PropTypes.object.isRequired,
  initialCourseContent: PropTypes.string,
  entityId: PropTypes.string.isRequired,
  banner: PropTypes.node,
}
